import { useState } from 'react';
import { supabase } from '../utils/supabaseClient';

export const usePaymentFlow = () => {
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const checkFeeWaiver = async (submissionId, userId) => {
    try {
      // Check if user has a fee waiver for this submission
      const { data: waiver, error } = await supabase
        .from('artist_submission_waivers')
        .select('*')
        .eq('user_id', userId)
        .eq('submission_id', submissionId)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
      
      return waiver || null;
    } catch (err) {
      console.error('Error checking fee waiver:', err);
      return null;
    }
  };

  const initiatePayment = async (paymentData) => {
    const { userId, stripeProductId, submissionId } = paymentData;
    
    try {
      setPaymentLoading(true);
      setPaymentError(null);

      // Check for fee waiver first
      const feeWaiver = await checkFeeWaiver(submissionId, userId);
      
      if (feeWaiver) {
        // Skip payment, directly submit application
        console.log('✅ Fee waiver found, skipping payment');
        return { success: true, feeWaived: true };
      }

      console.log('🔧 Payment Flow: Initiating payment with data:', paymentData);
      console.log('🔧 Using supabase.functions.invoke approach (like CollectionDetailAdmin)');

      // Use the same approach as CollectionDetailAdmin - supabase.functions.invoke
      const { data, error } = await supabase.functions.invoke('generate_payment_link', {
        body: {
          userId,
          submissionId
        }
      });

      console.log('🔧 Edge function response:', { data, error });

      if (error) {
        console.error('🔧 Edge function error:', error);
        throw new Error(error.message);
      }

      if (data && data.paymentUrl) {
        console.log('✅ Payment link generated successfully:', data.paymentUrl);
        // Redirect to Stripe payment page
        console.log('🔄 Redirecting to Stripe:', data.paymentUrl);
        window.location.href = data.paymentUrl;
        return { success: true, paymentUrl: data.paymentUrl };
      } else {
        console.error('🔧 Edge function returned no payment URL:', data);
        throw new Error('No payment URL received from server');
      }
    } catch (err) {
      console.error('❌ Payment initiation error:', err);
      setPaymentError(err.message || 'Failed to initiate payment');
      return { success: false, error: err.message };
    } finally {
      setPaymentLoading(false);
    }
  };

  const checkPaymentStatus = async (submissionId, userId) => {
    try {
      // This would typically call an endpoint to check payment status
      // For now, we'll return a mock response
      return { paid: false, status: 'pending' };
    } catch (err) {
      console.error('Payment status check error:', err);
      return { paid: false, status: 'error' };
    }
  };

  const resetPaymentError = () => {
    setPaymentError(null);
  };

  return {
    paymentLoading,
    paymentError,
    initiatePayment,
    checkPaymentStatus,
    resetPaymentError,
    checkFeeWaiver
  };
};
